import { useState, useEffect } from 'react'
import {
	Box,
	Drawer,
	IconButton,
	Tooltip
} from '@mui/material'
import { supabase } from '../utils/supabase'
import {
	Close as CloseIcon,
	ChevronLeft as ChevronLeftIcon,
	ChevronRight as ChevronRightIcon
} from '@mui/icons-material'
import {
	Calendar,
	BookOpen,
	PawPrint,
	Users,
	List,
	MessageSquare,
	LayoutPanelLeft,
	Settings,
	HelpCircle,
	LogOut,
	LayoutDashboard,
	FileText,
	Receipt,
	ShieldCheck
} from 'lucide-react'

// Nami lavender theme tokens (from the design handoff)
const COLOR = {
	bg: '#FFFFFF',
	surface: '#F5F3FB',
	border: '#E7E2F3',
	text: '#2E2A40',
	muted: '#8B85A4',
	primarySoft: '#EDE9FB',
	primaryDeep: '#5F4EC2'
}

const FONT = "'Outfit', sans-serif"

function NavigationSidebar({
	open,
	onClose,
	selectedTab,
	onTabChange,
	isMobile,
	merchant,
	pendingAppointments,
	onPendingAppointmentsChange,
	unreadMessagesCount = 0,
	showAdmin = false
}) {
	const [isCollapsed, setIsCollapsed] = useState(false)

	// Fetch pending appointments with full data
	useEffect(() => {
		if (!merchant?.id) return

		const fetchPendingAppointments = async () => {
			try {
				const { data, error } = await supabase
					.from('appointments')
					.select(`
						*,
						service_types!inner(name, duration_minutes, price, appointment_color),
						employees!inner(first_name, last_name),
						pets(name, breed)
					`)
					.eq('merchant_id', merchant.id)
					.eq('status', 'bookPending')
					// Include expired (past) requests too — the merchant still needs to
					// clear them (Dismiss). PendingAppointments / NotificationsPanel show
					// a Dismiss-only action for those.
					.order('start_ts', { ascending: true })

				if (error) throw error
				onPendingAppointmentsChange(data || [])
			} catch (error) {
				console.error('Error fetching pending appointments:', error)
			}
		}

		fetchPendingAppointments()

		// Set up real-time subscription for updates
		const channel = supabase
			.channel('pending-appointments-realtime')
			.on(
				'postgres_changes',
				{
					event: '*',
					schema: 'public',
					table: 'appointments',
					filter: `merchant_id=eq.${merchant.id}`
				},
				() => {
					fetchPendingAppointments()
				}
			)
			.subscribe()

		return () => {
			supabase.removeChannel(channel)
		}
	}, [merchant?.id, onPendingAppointmentsChange])

	// Count pending requests by group, so a multi-pet grouped request counts as
	// one (matching how PendingAppointments / NotificationsPanel display them).
	const pendingRequestCount = pendingAppointments
		? new Set(pendingAppointments.map(a => a.group_id || a.id)).size
		: 0

	const navigationItems = [
		{
			label: 'Dashboard',
			value: 8,
			icon: <LayoutDashboard size={17} strokeWidth={1.5} />
		},
		{
			label: 'Calendar',
			value: 0,
			icon: <Calendar size={17} strokeWidth={1.5} />
		},
		{
			label: 'Bookings',
			value: 4,
			icon: <BookOpen size={17} strokeWidth={1.5} />,
			badge: pendingRequestCount > 0 ? pendingRequestCount : null
		},
		{
			label: 'Invoices',
			value: 10,
			icon: <Receipt size={17} strokeWidth={1.5} />
		},
		{
			label: 'Clients/Pets',
			value: 1,
			icon: <PawPrint size={17} strokeWidth={1.5} />
		},
		{
			label: 'Staff',
			value: 2,
			icon: <Users size={17} strokeWidth={1.5} />
		},
		{
			label: 'Services',
			value: 3,
			icon: <List size={17} strokeWidth={1.5} />
		},
		{
			label: 'Messages',
			value: 5,
			icon: <MessageSquare size={17} strokeWidth={1.5} />,
			// Capped to "9+" by the existing badge renderer below (see collapsed
			// state). Pass null when zero so the badge bubble doesn't render.
			badge: unreadMessagesCount > 0
				? (unreadMessagesCount > 9 ? '9+' : unreadMessagesCount)
				: null
		},
		{
			label: 'Agreements',
			value: 9,
			icon: <FileText size={17} strokeWidth={1.5} />
		},
		{
			label: 'My Landing Page',
			value: 7,
			icon: <LayoutPanelLeft size={17} strokeWidth={1.5} />
		},
		// Only merchants flagged is_admin (migration 39) get the Admin tab, where
		// they pick a merchant to act as. Hidden while already acting as one.
		...(showAdmin ? [{
			label: 'Admin',
			value: 11,
			icon: <ShieldCheck size={17} strokeWidth={1.5} />
		}] : [])
	]

	const settingsItems = [
		{
			label: 'Settings',
			value: 6,
			icon: <Settings size={17} strokeWidth={1.5} />
		},
		{
			label: 'Support',
			value: null,
			disabled: true,
			icon: <HelpCircle size={17} strokeWidth={1.5} />
		},
		{
			label: 'Logout',
			value: 'logout',
			icon: <LogOut size={17} strokeWidth={1.5} />
		}
	]

	const handleLogout = async () => {
		try {
			const { error } = await supabase.auth.signOut()
			if (error) throw error

			// Clear any local storage or session data if needed
			localStorage.clear()

			// Redirect to login page
			window.location.href = '/login'
		} catch (error) {
			console.error('Error logging out:', error)
			alert('Failed to log out. Please try again.')
		}
	}

	const handleItemClick = (value) => {
		if (value === 'logout') {
			handleLogout()
		} else if (value !== null) {
			onTabChange(null, value)
			if (isMobile) {
				onClose()
			}
		}
	}

	const handleToggleCollapse = () => {
		setIsCollapsed(!isCollapsed)
	}

	const renderNavigationItem = (item) => {
		const isActive = selectedTab === item.value
		const isDisabled = item.disabled || (item.value === null)

		const itemContent = (
			<Box
				onClick={() => !isDisabled && handleItemClick(item.value)}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: isCollapsed && !isMobile ? 0 : '11px',
					justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
					p: isCollapsed && !isMobile ? '9px 0' : '9px 10px',
					height: '39px',
					borderRadius: '10px',
					bgcolor: isActive ? COLOR.primarySoft : 'transparent',
					cursor: isDisabled ? 'default' : 'pointer',
					opacity: isDisabled ? 0.65 : 1,
					color: isActive ? COLOR.primaryDeep : COLOR.text,
					'&:hover': {
						bgcolor: isDisabled ? 'transparent' : (isActive ? COLOR.primarySoft : COLOR.surface)
					},
					transition: 'all 0.18s ease',
					boxSizing: 'border-box'
				}}
			>
				<Box
					sx={{
						width: 17,
						height: 17,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexShrink: 0,
						opacity: 0.85,
						color: 'inherit'
					}}
				>
					{item.icon}
				</Box>
				{(!isCollapsed || isMobile) && (
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						flex: 1,
						overflow: 'hidden'
					}}>
						<Box sx={{
							fontFamily: FONT,
							fontSize: '14px',
							fontWeight: isActive ? 700 : 600,
							lineHeight: 1.4,
							color: 'inherit',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis'
						}}>
							{item.label}
						</Box>
						{item.badge && (
							<Box
								sx={{
									borderRadius: '12px',
									bgcolor: '#EF4444',
									height: '20px',
									width: '20px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center'
								}}
							>
								<Box
									component="span"
									sx={{
										color: 'white',
										fontSize: '11px',
										fontWeight: 700,
										fontFamily: FONT,
										lineHeight: 1,
										textAlign: 'center',
										display: 'block',
										padding: 0,
										margin: 0
									}}
								>
									{item.badge}
								</Box>
							</Box>
						)}
					</Box>
				)}
			</Box>
		)

		// Wrap with tooltip when collapsed on desktop
		if (isCollapsed && !isMobile) {
			const tooltipTitle = item.badge ? `${item.label} (${item.badge})` : item.label
			return (
				<Tooltip key={item.value || item.label} title={tooltipTitle} placement="right">
					<Box sx={{ position: 'relative' }}>
						{itemContent}
						{item.badge && (
							<Box
								sx={{
									position: 'absolute',
									top: '-4px',
									right: '-4px',
									width: '20px',
									height: '20px',
									borderRadius: '50%',
									bgcolor: '#EF4444',
									border: '2px solid white',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center'
								}}
							>
								<Box
									component="span"
									sx={{
										color: 'white',
										fontSize: '10px',
										fontWeight: 700,
										fontFamily: FONT,
										lineHeight: 1,
										textAlign: 'center',
										display: 'block',
										padding: 0,
										margin: 0
									}}
								>
									{item.badge > 9 ? '9+' : item.badge}
								</Box>
							</Box>
						)}
					</Box>
				</Tooltip>
			)
		}

		return <Box key={item.value || item.label}>{itemContent}</Box>
	}

	const sidebarContent = (
		<Box sx={{
			p: isCollapsed && !isMobile ? '14px 10px' : '14px 12px',
			display: 'flex',
			flexDirection: 'column',
			height: '100%',
			width: '100%'
		}}>
			{/* Header */}
			<Box sx={{
				display: 'flex',
				justifyContent: isCollapsed && !isMobile ? 'center' : 'space-between',
				alignItems: 'center',
				gap: '6px',
				p: isCollapsed && !isMobile ? '2px 0 12px 0' : '2px 4px 12px 8px',
				borderBottom: `1px solid ${COLOR.border}`,
				mb: '12px'
			}}>
				{(!isCollapsed || isMobile) && (
					<Box
						sx={{
							fontFamily: FONT,
							fontSize: '21px',
							lineHeight: 1.2,
							letterSpacing: '0.01em',
							color: COLOR.text,
							fontWeight: 600
						}}
					>
						Nami
					</Box>
				)}
				{isMobile ? (
					<IconButton
						onClick={onClose}
						sx={{
							width: 26,
							height: 26,
							bgcolor: 'transparent',
							borderRadius: '7px',
							color: COLOR.muted,
							'&:hover': {
								bgcolor: COLOR.surface,
								color: COLOR.text
							}
						}}
					>
						<CloseIcon sx={{ fontSize: 16 }} />
					</IconButton>
				) : (
					<IconButton
						onClick={handleToggleCollapse}
						sx={{
							width: 26,
							height: 26,
							bgcolor: 'transparent',
							borderRadius: '7px',
							color: COLOR.muted,
							'&:hover': {
								bgcolor: COLOR.surface,
								color: COLOR.text
							}
						}}
					>
						{isCollapsed ? (
							<ChevronRightIcon sx={{ fontSize: 16 }} />
						) : (
							<ChevronLeftIcon sx={{ fontSize: 16 }} />
						)}
					</IconButton>
				)}
			</Box>

			{/* Navigation Items */}
			<Box sx={{
				flexGrow: 1,
				overflowY: 'auto',
				overflowX: 'hidden',
				display: 'flex',
				flexDirection: 'column',
				gap: '2px'
			}}>
				{navigationItems.map(item => renderNavigationItem(item))}

				<Box sx={{ height: '1px', bgcolor: COLOR.border, m: '12px 4px', flexShrink: 0 }} />

				{settingsItems.map(item => renderNavigationItem(item))}
			</Box>
		</Box>
	)

	// Mobile: Drawer overlay
	if (isMobile) {
		return (
			<Drawer
				anchor="left"
				open={open}
				onClose={onClose}
				sx={{
					'& .MuiDrawer-paper': {
						width: 280,
						bgcolor: COLOR.bg,
						borderRight: `1px solid ${COLOR.border}`
					}
				}}
			>
				{sidebarContent}
			</Drawer>
		)
	}

	// Desktop: Permanent sidebar
	return (
		<Box
			sx={{
				width: isCollapsed ? 72 : 218,
				flexShrink: 0,
				bgcolor: COLOR.bg,
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				transition: 'width 0.18s ease',
				borderRight: `1px solid ${COLOR.border}`
			}}
		>
			{sidebarContent}
		</Box>
	)
}

export default NavigationSidebar
